import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo, useState } from 'react';
import { GET_CARDS_CUSTOMER_DATA_QUERY, GET_CARD_DISPLAY_FIELDS_QUERY } from '../../integrations/allkort/graphql/gql/CustomerQueries';
import { Card, CardsCustomerDataResponse } from '../../integrations/allkort/graphql/interface/Customer';

const CARDS_PER_PAGE = 10; // Number of cards to fetch per page - tuned to stay under the API's field cost limit (HC0047)
const MAX_CARDS = 500;

/**
 * Custom hook for fetching ALL cards with automatic pagination
 * Will fetch up to MAX_CARDS (500) cards by following pagination cursors
 * 
 * Usage:
 * const { cards, loading, error } = useGetAllCards();
 * 
 * @returns Object with cards array, loading state, and error
 */
export const useGetAllCards = () => {
	const [allCards, setAllCards] = useState<Card[]>([]);
	const [currentCursor, setCurrentCursor] = useState<string | null>(null);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isComplete, setIsComplete] = useState(false);

	const variables = useMemo(() => ({
		first: CARDS_PER_PAGE,
		after: currentCursor,
		before: null,
		last: null,
	}), [currentCursor]);

	const { data, loading, error, refetch: apolloRefetch } = useQuery<CardsCustomerDataResponse>(
		GET_CARDS_CUSTOMER_DATA_QUERY,
		{
			variables,
			fetchPolicy: 'network-only',
			skip: isComplete,
		}
	);

	const [displayFieldsCursor, setDisplayFieldsCursor] = useState<string | null>(null);
	const [displayFieldsById, setDisplayFieldsById] = useState<Map<string, Pick<Card, 'name' | 'maskedPan' | 'reference1' | 'reference2'>>>(new Map());
	const [displayFieldsComplete, setDisplayFieldsComplete] = useState(false);

	// Fetch display fields separately in small pages to keep large card collections complete.
	const { data: displayFieldsData, loading: displayFieldsLoading, error: displayFieldsError } = useQuery<CardsCustomerDataResponse>(
		GET_CARD_DISPLAY_FIELDS_QUERY,
		{
			variables: { first: CARDS_PER_PAGE, after: displayFieldsCursor },
			fetchPolicy: 'network-only',
			skip: !isComplete || allCards.length === 0,
		}
	);

	if (displayFieldsError) {
		console.error('[useGetAllCards] Display fields query error:', displayFieldsError);
	}

	// Merge display fields into cards when they arrive
	useEffect(() => {
		if (!displayFieldsData?.currentCustomer?.cards?.nodes) {
			return;
		}

		const pageDisplayFieldsById = new Map(
			displayFieldsData.currentCustomer.cards.nodes.map(card => [
				card.id,
				{ name: card.name, maskedPan: card.maskedPan, reference1: card.reference1, reference2: card.reference2 }
			])
		);

		setDisplayFieldsById(prev => new Map([...prev, ...pageDisplayFieldsById]));
		if (displayFieldsData.currentCustomer.cards.pageInfo.hasNextPage) {
			setDisplayFieldsCursor(displayFieldsData.currentCustomer.cards.pageInfo.endCursor);
		} else {
			setDisplayFieldsComplete(true);
		}
	}, [displayFieldsData]);

	useEffect(() => {
		if (!isComplete || displayFieldsById.size === 0) return;

		setAllCards(prev => prev.map(card => {
			const displayFields = displayFieldsById.get(card.id);
			return displayFields ? { ...card, ...displayFields } : card;
		}));
	}, [isComplete, displayFieldsById]);

	useEffect(() => {
		if (!data?.currentCustomer?.cards) return;

		const { nodes, pageInfo } = data.currentCustomer.cards;

		if (nodes && nodes.length > 0) {
			setAllCards(prev => {
				// If this is the first fetch (no cursor), replace all cards
				if (!currentCursor) {
					return nodes;
				}
				// Otherwise append new cards, avoiding duplicates
				const existingIds = new Set(prev.map(card => card.id));
				const newCards = nodes.filter(card => !existingIds.has(card.id));
				return [...prev, ...newCards];
			});

			// Check if we should fetch more
			const totalCards = currentCursor ? allCards.length + nodes.length : nodes.length;

			if (pageInfo.hasNextPage && totalCards < MAX_CARDS) {
				setCurrentCursor(pageInfo.endCursor);
				setIsFetchingMore(true);
			} else {
				setIsFetchingMore(false);
				setIsComplete(true);
			}
		} else {
			setIsFetchingMore(false);
			setIsComplete(true);
		}
	}, [data, currentCursor]);

	// KEY FIX: do NOT clear allCards here. Clearing it causes CardsPage's merge
	// effect to fire with an empty regular-cards array while bankCardsData is still
	// present, showing only bank cards. Old cards stay visible during the in-flight
	// request and are replaced atomically when new data arrives (cursor===null branch).
	const refetch = () => {
		setCurrentCursor(null);
		setDisplayFieldsCursor(null);
		setDisplayFieldsById(new Map());
		setDisplayFieldsComplete(false);
		setIsComplete(false);
		setIsFetchingMore(false);
		apolloRefetch({ first: CARDS_PER_PAGE, after: null, before: null, last: null });
	};

	return {
		cards: allCards,
		loading: loading || isFetchingMore || (isComplete && allCards.length > 0 && (!displayFieldsComplete || displayFieldsLoading)),
		error,
		isComplete,
		refetch,
	};
};
